import { useState, useEffect } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultImages = [
  {
    id: "1",
    title: "Our Palm Plantation",
    description: "Lush palm trees at AfriGrow Farm",
    image_url: "https://images.unsplash.com/photo-1671208615878-c074b4bf08dc?w=800",
    category: "farm"
  },
  {
    id: "2",
    title: "Fresh Harvest",
    description: "Fresh vegetables ready for market",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/2mv30pjo_The-Secret-History-of-Okra-1.jpg",
    category: "produce"
  },
  {
    id: "3",
    title: "Pig Farm",
    description: "Our well-maintained pig pens",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54tu1wv0_pig-photo111.jpg",
    category: "livestock"
  },
  {
    id: "4",
    title: "Fish Ponds",
    description: "Aquaculture ponds at AfriGrow",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/wnfypbzf_WhatsApp%20Image%202026-04-01%20at%2010.59.50%20AM.jpeg",
    category: "aquaculture"
  },
  {
    id: "5",
    title: "Vegetable Garden",
    description: "Organic vegetable production",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/9wkbtvnt_il_570xN.5642352405_4fd5.webp",
    category: "produce"
  },
  {
    id: "6",
    title: "Grasscutter",
    description: "Our grasscutter breeding program",
    image_url: "https://customer-assets.emergentagent.com/job_igbodu-agri/artifacts/54lc9buf_image.png",
    category: "livestock"
  }
];

const categories = [
  { value: "all", label: "All" },
  { value: "farm", label: "Farm" },
  { value: "livestock", label: "Livestock" },
  { value: "produce", label: "Produce" },
  { value: "aquaculture", label: "Aquaculture" }
];

export default function GalleryPage() {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    if (selectedCategory === "all") {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.category === selectedCategory));
    }
  }, [selectedCategory, images]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      const galleryData = response.data.length > 0 ? response.data : defaultImages;
      setImages(galleryData);
      setFilteredImages(galleryData);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      setImages(defaultImages);
      setFilteredImages(defaultImages);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(filteredImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  return (
    <div className="pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="section-padding bg-stone-900 text-white" data-testid="gallery-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="font-accent text-sm uppercase tracking-widest text-gold mb-6 block">
              Photo Gallery
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Life at AfriGrow Farm
            </h1>
            <p className="text-stone-300 text-lg md:text-xl leading-relaxed">
              Take a visual tour of our farm. See our livestock, produce, and the beautiful 
              environment where we grow and raise quality agricultural products.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-stone-50" data-testid="gallery-grid-section">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.value)}
                className={`rounded-full ${
                  selectedCategory === category.value 
                    ? "bg-green-600 hover:bg-green-700 text-white" 
                    : "border-stone-300 hover:border-green-600 hover:text-green-600"
                }`}
                data-testid={`gallery-filter-${category.value}`}
              >
                {category.label}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] bg-stone-200 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-shadow"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.image_url}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="font-heading text-xl font-semibold text-white mb-1">
                      {image.title}
                    </h3>
                    <p className="text-stone-300 text-sm">
                      {image.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl p-0 bg-stone-900 border-none" aria-describedby="gallery-image-description">
          <DialogTitle className="sr-only">
            {selectedImage?.title || "Gallery Image"}
          </DialogTitle>
          <p id="gallery-image-description" className="sr-only">
            {selectedImage?.description || "View full size image from AfriGrow Farm gallery"}
          </p>
          {selectedImage && (
            <div className="relative">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain"
              />
              
              {/* Navigation */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="h-6 w-6 text-white" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="h-6 w-6 text-white" />
              </button>
              
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                data-testid="lightbox-close"
              >
                <X className="h-6 w-6 text-white" />
              </button>
              
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-900">
                <h3 className="font-heading text-2xl font-semibold text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-stone-300">
                  {selectedImage.description}
                </p>
                <p className="text-stone-500 text-sm mt-2">
                  {currentIndex + 1} / {filteredImages.length}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="section-padding bg-green-600" data-testid="gallery-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Want to See More?
          </h2>
          <p className="text-green-100 text-lg max-w-2xl mx-auto mb-10">
            Schedule a farm visit to experience AfriGrow Farm in person. See our operations 
            up close and meet our team.
          </p>
          <Button 
            asChild 
            size="lg"
            className="bg-white text-green-700 hover:bg-stone-100 rounded-full px-8 py-6 text-lg font-semibold"
            data-testid="schedule-visit-gallery-btn"
          >
            <a href="/contact">Schedule a Visit</a>
          </Button>
        </div>
      </section>
    </div>
  );
}
